import { useState } from "react";
import { askToConfirm } from "../components/confirm";
import { useNavigate } from "react-router-dom";
import { Icon } from "../components/icons";
import { toast } from "../components/toast";
import { useMe } from "../lib/me";
import {
  useMembers, useInviteMember, usePatchMember, useResendInvite,
  useRemoveMember, useClaimLink, useClerkCheck, useSelfProofer, useSetMyName,
} from "../lib/proofers";
import type { Member } from "../api/types";

/** Click-to-edit name: shows the name with a pencil; clicking swaps in an input
 *  that saves on Enter/blur and cancels on Escape. `placeholder` is what an empty
 *  name reads as (e.g. a proofer invited without one). */
function NameEdit({ value, placeholder, onSave }: { value: string; placeholder: string; onSave: (v: string) => void }) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <input
        className="team-nameinput"
        autoFocus
        defaultValue={value}
        placeholder={placeholder}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          if (e.key === "Escape") { e.stopPropagation(); setEditing(false); }
        }}
        onBlur={(e) => {
          const next = e.target.value.trim();
          setEditing(false);
          if (next !== value) onSave(next);
        }}
      />
    );
  }
  return (
    <button className="team-nameedit" title="Rename" onClick={() => setEditing(true)}>
      <span className={value ? "" : "team-nameempty"}>{value || placeholder}</span>
      <Icon name="edit" size={12} />
    </button>
  );
}

// A member is `active` the moment they first sign in (claim-on-login binds their
// account) — surfaced as "Live" so the roster reads as a state of the person.
const STATUS_LABEL: Record<string, string> = {
  invited: "Invited",
  active: "Live",
  disabled: "Disabled",
};

const ROLE_LABEL: Record<string, string> = { admin: "Admin", proofer: "Proofer" };
const ROLE_ORDER = ["admin", "proofer"];

/** Settings → Team — the one place membership and roles live. Invite people,
 *  set who's an admin and who proofs, and grant yourself the proofer role so
 *  you can pick up work too. Each person's *log* still opens from the Proofers
 *  page (a member row here links straight to it). */
export function TeamSettings() {
  const { data: me } = useMe();
  const { data: roster, isLoading } = useMembers();
  const members = roster?.members ?? [];
  const clerkLookup = roster?.clerk_lookup ?? true;
  // The caller manages themselves in the "You" panel (driven by `me`); everyone
  // else is the roster below.
  const others = members.filter((m) => !m.is_self);

  const invite = useInviteMember();
  const patch = usePatchMember();
  const resend = useResendInvite();
  const remove = useRemoveMember();
  const claimLink = useClaimLink();
  const clerkCheck = useClerkCheck();
  const selfProofer = useSelfProofer();
  const setMyName = useSetMyName();
  const nav = useNavigate();

  const renameMe = (name: string) =>
    setMyName.mutate(name, {
      onSuccess: () => toast("Name updated", "success"),
      onError: (e: unknown) => toast((e as Error)?.message || "Couldn't update", "error"),
    });
  const renameMember = (m: Member, name: string) =>
    patch.mutate(
      { id: m.id, patch: { name } },
      {
        onSuccess: () => toast("Name updated", "success"),
        onError: (e: unknown) => toast((e as Error)?.message || "Couldn't rename", "error"),
      },
    );

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [roles, setRoles] = useState<string[]>(["proofer"]);

  const iProof = !!me?.entitlements?.proofer;

  const toggleInviteRole = (r: string) =>
    setRoles((cur) => (cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]));

  const doInvite = () => {
    if (!email.trim()) return;
    invite.mutate(
      { email: email.trim(), name: name.trim() || undefined, roles: roles.length ? roles : ["proofer"] },
      {
        onSuccess: () => { toast("Invite sent", "success"); setEmail(""); setName(""); setRoles(["proofer"]); },
        onError: (e: unknown) => toast((e as Error)?.message || "Couldn't invite", "error"),
      },
    );
  };

  // Toggle a role on an existing member. The server keeps at least "proofer" if
  // you clear everything, so a member is never left with no capability.
  const toggleMemberRole = (m: Member, r: string) => {
    const next = m.roles.includes(r) ? m.roles.filter((x) => x !== r) : [...m.roles, r];
    patch.mutate(
      { id: m.id, patch: { roles: next } },
      { onError: (e: unknown) => toast((e as Error)?.message || "Couldn't update roles", "error") },
    );
  };

  const doResend = (id: string) =>
    resend.mutate(id, {
      onSuccess: () => toast("Invite resent", "success"),
      onError: () => toast("Couldn't resend", "error"),
    });

  const doClaimLink = (id: string) =>
    claimLink.mutate(id, {
      onSuccess: (r) =>
        navigator.clipboard?.writeText(r.claim_url).then(
          () => toast("Claim link copied — send it to them", "success"),
          () => toast(r.claim_url, "info"),
        ),
      onError: (e: unknown) => toast((e as Error)?.message || "Couldn't create a link", "error"),
    });

  const doWithdraw = async (m: Member) => {
    const who = m.name || m.email;
    if (!(await askToConfirm(`Remove ${who} from the team? They'll need to be invited again.`, { yes: "Remove", danger: true }))) return;
    remove.mutate(m.id, {
      onSuccess: () => toast("Removed", "success"),
      onError: (e: unknown) => toast((e as Error)?.message || "Couldn't remove", "error"),
    });
  };

  const doSelfProofer = () =>
    selfProofer.mutate(!iProof, {
      onSuccess: () => toast(iProof ? "You'll no longer be assigned proofing" : "You can now be assigned proofing", "success"),
      onError: (e: unknown) => toast((e as Error)?.message || "Couldn't update", "error"),
    });

  return (
    <div className="pl-page">
      <header className="pl-head">
        <h1>Team</h1>
        <p className="muted">
          Everyone who can use the tool, and what they can do. Invite people, set their roles,
          and open a proofer to see their log.
        </p>
      </header>

      {/* --- You ------------------------------------------------------------ */}
      <section className="team-you">
        <div className="team-you-id">
          <span className="pl-rav">{(me?.name || me?.email || "?").slice(0, 1).toUpperCase()}</span>
          <span className="pl-rmain">
            <span className="pl-rname">
              <NameEdit value={me?.name || ""} placeholder={me?.email || "Your name"} onSave={renameMe} />
              <span className="team-youtag">You</span>
            </span>
            {me?.email && <span className="pl-remail">{me.email}</span>}
          </span>
        </div>
        <div className="team-you-roles">
          {(me?.roles?.includes("admin")) && <span className="team-chip on locked" title="You're an admin">Admin</span>}
          <span className={`team-chip${iProof ? " on" : ""}`}>Proofer</span>
        </div>
        <button className={`pl-btn${iProof ? "" : " primary"}`} onClick={doSelfProofer} disabled={selfProofer.isPending}>
          <Icon name="headphones" size={14} />
          {iProof ? "Stop proofing" : "Also work as a proofer"}
        </button>
      </section>
      {iProof && (
        <p className="team-hint">
          You'll appear as a proofer on the Workflows board and can assign work to yourself —
          it shows up in <b>My Work</b>.
        </p>
      )}

      {/* --- Clerk connection self-check (membership diagnostic) ------------ */}
      <div className="pl-diag">
        <button className="pl-btn sm" onClick={() => clerkCheck.mutate(undefined)} disabled={clerkCheck.isPending}>
          {clerkCheck.isPending ? "Checking…" : "Check Clerk connection"}
        </button>
        {clerkCheck.data && (
          <span className="pl-diagout">
            keys: <b>{clerkCheck.data.publishable_env}</b> / <b>{clerkCheck.data.secret_env}</b>
            {" · "}
            {clerkCheck.data.instances_match
              ? "same instance ✓"
              : "⚠ keys are from DIFFERENT Clerk instances — that's why accounts can't be matched"}
            {" · API: "}
            {clerkCheck.data.backend_api.ok
              ? "reachable ✓"
              : `failed — ${clerkCheck.data.backend_api.detail ?? "unknown"}`}
          </span>
        )}
        {clerkCheck.isError && <span className="pl-diagout">Couldn't run the check.</span>}
      </div>

      {!clerkLookup && (
        <div className="pl-warn">
          Clerk lookup is unavailable (no Backend API key configured), so accounts can't be
          linked automatically. Use <strong>Copy link</strong> on a member to link them.
        </div>
      )}

      {/* --- Invite -------------------------------------------------------- */}
      <div className="team-invite">
        <div className="pl-invite">
          <input placeholder="teammate@email.com" value={email} type="email"
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doInvite()} />
          <input placeholder="Name (optional)" value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doInvite()} />
          <button className="pl-btn primary" onClick={doInvite} disabled={invite.isPending}>
            <Icon name="plus" size={14} /> Invite
          </button>
        </div>
        <div className="team-inviteroles">
          <span className="muted">Roles:</span>
          {ROLE_ORDER.map((r) => (
            <button key={r} className={`team-chip btn${roles.includes(r) ? " on" : ""}`}
              onClick={() => toggleInviteRole(r)} type="button">
              {ROLE_LABEL[r]}
            </button>
          ))}
        </div>
      </div>

      {/* --- Roster -------------------------------------------------------- */}
      {isLoading ? (
        <p className="muted">Loading…</p>
      ) : others.length === 0 ? (
        <div className="pl-empty">No one else on the team yet — invite someone above.</div>
      ) : (
        <div className="pl-roster">
          {others.map((m) => (
            <div key={m.id} className="pl-rrow">
              <span className="pl-rav">{(m.name || m.email).slice(0, 1).toUpperCase()}</span>
              <span className="pl-rmain">
                <span className="pl-rname">
                  <NameEdit value={m.name} placeholder={m.email} onSave={(v) => renameMember(m, v)} />
                </span>
                <span className="pl-remail">{m.email}</span>
              </span>

              <div className="team-roles">
                {ROLE_ORDER.map((r) => (
                  <button key={r} className={`team-chip btn${m.roles.includes(r) ? " on" : ""}`}
                    onClick={() => toggleMemberRole(m, r)} disabled={patch.isPending}
                    title={m.roles.includes(r) ? `Remove ${ROLE_LABEL[r]}` : `Make ${ROLE_LABEL[r]}`}>
                    {ROLE_LABEL[r]}
                  </button>
                ))}
              </div>

              <span className={`pl-badge s-${m.status}`}>{STATUS_LABEL[m.status] ?? m.status}</span>

              {m.roles.includes("proofer") && m.clerk_bound && (
                <button className="pl-riconbtn" title="Open this proofer's log" onClick={() => nav(`/proofers/${m.id}`)}>
                  <Icon name="external" size={15} />
                </button>
              )}
              {!m.clerk_bound && (
                <button className="pl-riconbtn" title="Copy a one-time link that links their account"
                  onClick={() => doClaimLink(m.id)}>
                  <Icon name="copy" size={15} />
                </button>
              )}
              {m.status === "invited" && (
                <div className="pl-ractions">
                  <button className="pl-riconbtn" title="Resend invite" onClick={() => doResend(m.id)}>
                    <Icon name="rotate-cw" size={15} />
                  </button>
                </div>
              )}
              <button className="pl-riconbtn danger" title="Remove from team" onClick={() => doWithdraw(m)}>
                <Icon name="x" size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
